import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';

const SuperAdminPricingRules = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('discount-rules');
  const [isSaving, setIsSaving] = useState(false);

  const tabs = [
    { id: 'discount-rules', label: 'Discount Rules', icon: 'Percent' },
    { id: 'coupon-management', label: 'Coupon Management', icon: 'Tag' },
    { id: 'regional-pricing', label: 'Regional Pricing', icon: 'Globe' },
    { id: 'dynamic-pricing', label: 'Dynamic Pricing', icon: 'TrendingUp' }
  ];

  // Discount Rules State
  const [discountRules, setDiscountRules] = useState([
    {
      id: 1,
      name: 'Annual Subscription Discount',
      type: 'percentage',
      value: 20,
      conditions: ['plan_type:all', 'duration:yearly'],
      status: 'active'
    },
    {
      id: 2,
      name: 'Enterprise Bulk Discount',
      type: 'percentage',
      value: 15,
      conditions: ['plan_type:enterprise', 'users:>50'],
      status: 'active'
    },
    {
      id: 3,
      name: 'Seasonal Promotion',
      type: 'fixed',
      value: 5000,
      conditions: ['date_range:2026-03-01:2026-03-31'],
      status: 'inactive'
    }
  ]);

  // Coupon State
  const [coupons, setCoupons] = useState([
    {
      id: 1,
      code: 'LAUNCH50',
      discount: 50,
      type: 'percentage',
      expiryDate: '2026-03-31',
      usageLimit: 100,
      usedCount: 47,
      minPurchase: 2999,
      status: 'active'
    },
    {
      id: 2,
      code: 'GROWTH20',
      discount: 20,
      type: 'percentage',
      expiryDate: '2026-12-31',
      usageLimit: 500,
      usedCount: 123,
      minPurchase: 0,
      status: 'active'
    },
    {
      id: 3,
      code: 'WELCOME1000',
      discount: 1000,
      type: 'fixed',
      expiryDate: '2026-06-30',
      usageLimit: 1000,
      usedCount: 342,
      minPurchase: 5000,
      status: 'active'
    }
  ]);

  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount: '',
    type: 'percentage',
    expiryDate: '',
    usageLimit: '',
    minPurchase: ''
  });

  // Regional Pricing State
  const [regionalPricing, setRegionalPricing] = useState([
    { id: 1, region: 'India', currency: 'INR', multiplier: 1.0, taxRate: 18, status: 'active' },
    { id: 2, region: 'United States', currency: 'USD', multiplier: 0.012, taxRate: 0, status: 'active' },
    { id: 3, region: 'United Kingdom', currency: 'GBP', multiplier: 0.0095, taxRate: 20, status: 'active' },
    { id: 4, region: 'European Union', currency: 'EUR', multiplier: 0.011, taxRate: 21, status: 'active' }
  ]);

  // Dynamic Pricing State
  const [dynamicPricing, setDynamicPricing] = useState({
    enabled: true,
    demandBased: true,
    competitorAnalysis: false,
    behaviorBased: true,
    abTesting: true,
    priceFloor: 10,
    priceCeiling: 50
  });

  const [showCouponForm, setShowCouponForm] = useState(false);
  const [showRuleForm, setShowRuleForm] = useState(false);

  const handleAddCoupon = () => {
    if (!newCoupon?.code || !newCoupon?.discount || !newCoupon?.expiryDate) {
      return;
    }

    const coupon = {
      id: Date.now(),
      code: newCoupon?.code?.toUpperCase(),
      discount: Number(newCoupon?.discount),
      type: newCoupon?.type,
      expiryDate: newCoupon?.expiryDate,
      usageLimit: Number(newCoupon?.usageLimit) || 0,
      usedCount: 0,
      minPurchase: Number(newCoupon?.minPurchase) || 0,
      status: 'active'
    };

    setCoupons([...coupons, coupon]);
    setNewCoupon({ code: '', discount: '', type: 'percentage', expiryDate: '', usageLimit: '', minPurchase: '' });
    setShowCouponForm(false);
  };

  const handleDeleteCoupon = (couponId) => {
    setCoupons(coupons?.filter(c => c?.id !== couponId));
  };

  const handleToggleCoupon = (couponId) => {
    setCoupons(coupons?.map(c => 
      c?.id === couponId ? { ...c, status: c?.status === 'active' ? 'inactive' : 'active' } : c
    ));
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      console.log('Pricing rules saved');
      setIsSaving(false);
    }, 1000);
  };

  const renderDiscountRules = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Discount Rules</h3>
        <Button
          variant="default"
          size="sm"
          onClick={() => setShowRuleForm(!showRuleForm)}
          iconName={showRuleForm ? 'X' : 'Plus'}
          iconPosition="left"
        >
          {showRuleForm ? 'Cancel' : 'Add Rule'}
        </Button>
      </div>

      {showRuleForm && (
        <div className="bg-muted/30 border border-border rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input label="Rule Name" type="text" placeholder="e.g., Black Friday Sale" />
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Discount Type</label>
              <select className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50">
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <Input label="Discount Value" type="number" placeholder="20" />
            <Input label="Conditions" type="text" placeholder="plan_type:growth" />
          </div>
          <Button variant="default" size="sm" iconName="Plus" iconPosition="left">
            Create Rule
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {discountRules?.map(rule => (
          <div key={rule?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="font-semibold text-foreground">{rule?.name}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    rule?.status === 'active' ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                  }`}>
                    {rule?.status}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="Percent" size={14} />
                    {rule?.type === 'percentage' ? `${rule?.value}%` : `₹${rule?.value}`}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Filter" size={14} />
                    {rule?.conditions?.length} conditions
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="p-2 hover:bg-muted rounded-md transition-smooth">
                  <Icon name="Edit" size={16} className="text-primary" />
                </button>
                <button className="p-2 hover:bg-destructive/10 rounded-md transition-smooth">
                  <Icon name="Trash2" size={16} className="text-destructive" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCouponManagement = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Coupon Codes</h3>
        <Button
          variant="default"
          size="sm"
          onClick={() => setShowCouponForm(!showCouponForm)}
          iconName={showCouponForm ? 'X' : 'Plus'}
          iconPosition="left"
        >
          {showCouponForm ? 'Cancel' : 'Add Coupon'}
        </Button>
      </div>

      {showCouponForm && (
        <div className="bg-muted/30 border border-border rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <Input
              label="Coupon Code"
              type="text"
              value={newCoupon?.code}
              onChange={(e) => setNewCoupon({ ...newCoupon, code: e?.target?.value?.toUpperCase() })}
              placeholder="SUMMER2026"
            />
            <Input
              label="Discount Value"
              type="number"
              value={newCoupon?.discount}
              onChange={(e) => setNewCoupon({ ...newCoupon, discount: e?.target?.value })}
              placeholder="25"
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Discount Type</label>
              <select
                value={newCoupon?.type}
                onChange={(e) => setNewCoupon({ ...newCoupon, type: e?.target?.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <Input
              label="Expiry Date"
              type="date"
              value={newCoupon?.expiryDate}
              onChange={(e) => setNewCoupon({ ...newCoupon, expiryDate: e?.target?.value })}
            />
            <Input
              label="Usage Limit"
              type="number"
              value={newCoupon?.usageLimit}
              onChange={(e) => setNewCoupon({ ...newCoupon, usageLimit: e?.target?.value })}
              placeholder="100"
            />
            <Input
              label="Min Purchase (₹)"
              type="number"
              value={newCoupon?.minPurchase}
              onChange={(e) => setNewCoupon({ ...newCoupon, minPurchase: e?.target?.value })}
              placeholder="2999"
            />
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={handleAddCoupon}
            iconName="Plus"
            iconPosition="left"
          >
            Create Coupon
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {coupons?.map(coupon => (
          <div key={coupon?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono font-bold text-lg text-foreground">{coupon?.code}</span>
                  <button
                    onClick={() => handleToggleCoupon(coupon?.id)}
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      coupon?.status === 'active' ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {coupon?.status}
                  </button>
                </div>
                <p className="text-sm text-primary font-semibold">
                  {coupon?.type === 'percentage' ? `${coupon?.discount}% OFF` : `₹${coupon?.discount} OFF`}
                </p>
              </div>
              <button
                onClick={() => handleDeleteCoupon(coupon?.id)}
                className="p-2 hover:bg-destructive/10 rounded-md transition-smooth"
              >
                <Icon name="Trash2" size={16} className="text-destructive" />
              </button>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>Expires:</span>
                <span className="font-medium">{new Date(coupon?.expiryDate)?.toLocaleDateString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Usage:</span>
                <span className="font-medium">{coupon?.usedCount} / {coupon?.usageLimit}</span>
              </div>
              {coupon?.minPurchase > 0 && (
                <div className="flex items-center justify-between">
                  <span>Min Purchase:</span>
                  <span className="font-medium">₹{coupon?.minPurchase?.toLocaleString()}</span>
                </div>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-border">
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-smooth"
                  style={{ width: `${(coupon?.usedCount / coupon?.usageLimit) * 100}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderRegionalPricing = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Regional Pricing Configuration</h3>
        <Button variant="default" size="sm" iconName="Plus" iconPosition="left">
          Add Region
        </Button>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Region</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Currency</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Multiplier</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Tax Rate</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {regionalPricing?.map(region => (
              <tr key={region?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="px-4 py-3 font-medium text-foreground">{region?.region}</td>
                <td className="px-4 py-3 text-foreground">{region?.currency}</td>
                <td className="px-4 py-3 text-foreground">{region?.multiplier}</td>
                <td className="px-4 py-3 text-foreground">{region?.taxRate}%</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    region?.status === 'active' ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                  }`}>
                    {region?.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button className="p-2 hover:bg-muted rounded-md transition-smooth">
                      <Icon name="Edit" size={16} className="text-primary" />
                    </button>
                    <button className="p-2 hover:bg-destructive/10 rounded-md transition-smooth">
                      <Icon name="Trash2" size={16} className="text-destructive" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDynamicPricing = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-foreground">Dynamic Pricing Logic</h3>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="space-y-6">
          <Checkbox
            checked={dynamicPricing?.enabled}
            onChange={() => setDynamicPricing({ ...dynamicPricing, enabled: !dynamicPricing?.enabled })}
            label="Enable Dynamic Pricing"
            description="Automatically adjust prices based on configured rules"
          />

          <div className="pl-6 space-y-4 border-l-2 border-border">
            <Checkbox
              checked={dynamicPricing?.demandBased}
              onChange={() => setDynamicPricing({ ...dynamicPricing, demandBased: !dynamicPricing?.demandBased })}
              label="Demand-Based Pricing"
              description="Adjust prices based on demand patterns and subscription velocity"
              disabled={!dynamicPricing?.enabled}
            />

            <Checkbox
              checked={dynamicPricing?.competitorAnalysis}
              onChange={() => setDynamicPricing({ ...dynamicPricing, competitorAnalysis: !dynamicPricing?.competitorAnalysis })}
              label="Competitor Analysis"
              description="Monitor competitor pricing and adjust accordingly"
              disabled={!dynamicPricing?.enabled}
            />

            <Checkbox
              checked={dynamicPricing?.behaviorBased}
              onChange={() => setDynamicPricing({ ...dynamicPricing, behaviorBased: !dynamicPricing?.behaviorBased })}
              label="Behavior-Based Pricing"
              description="Personalize pricing based on customer behavior and engagement"
              disabled={!dynamicPricing?.enabled}
            />

            <Checkbox
              checked={dynamicPricing?.abTesting}
              onChange={() => setDynamicPricing({ ...dynamicPricing, abTesting: !dynamicPricing?.abTesting })}
              label="A/B Testing Framework"
              description="Test different pricing strategies with customer segments"
              disabled={!dynamicPricing?.enabled}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
            <Input
              label="Price Floor (%)"
              type="number"
              value={dynamicPricing?.priceFloor}
              onChange={(e) => setDynamicPricing({ ...dynamicPricing, priceFloor: e?.target?.value })}
              description="Minimum discount allowed"
              disabled={!dynamicPricing?.enabled}
            />
            <Input
              label="Price Ceiling (%)"
              type="number"
              value={dynamicPricing?.priceCeiling}
              onChange={(e) => setDynamicPricing({ ...dynamicPricing, priceCeiling: e?.target?.value })}
              description="Maximum markup allowed"
              disabled={!dynamicPricing?.enabled}
            />
          </div>
        </div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="AlertTriangle" size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-foreground mb-1">Dynamic Pricing Impact</h4>
            <p className="text-sm text-muted-foreground">
              Dynamic pricing rules will automatically adjust subscription prices based on configured parameters. 
              Monitor performance metrics regularly to ensure optimal pricing strategy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'discount-rules':
        return renderDiscountRules();
      case 'coupon-management':
        return renderCouponManagement();
      case 'regional-pricing':
        return renderRegionalPricing();
      case 'dynamic-pricing':
        return renderDynamicPricing();
      default:
        return renderDiscountRules();
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar isCollapsed={sidebarCollapsed} />
      <div className={`flex-1 transition-smooth ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading flex items-center gap-3">
              <Icon name="DollarSign" size={32} className="text-primary" />
              Pricing Rules Management
            </h1>
            <p className="text-sm md:text-base text-muted-foreground font-body mt-2">
              Configure discount rules, coupons, regional pricing, and dynamic pricing strategies
            </p>
          </div>

          {/* Tabs Navigation */}
          <div className="mb-6 border-b border-border overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-smooth border-b-2 whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span className="hidden sm:inline">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="mb-6">
            {renderTabContent()}
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              variant="default"
              size="lg"
              onClick={handleSave}
              loading={isSaving}
              iconName="Save"
              iconPosition="left"
            >
              Save & Activate Rules
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPricingRules;